/**
 * Money formatting for display only (ADR-004/017).
 *
 * Amounts cross the wire as integers in minor units — halalas for SAR. Nothing
 * here ever does arithmetic on them: the server computes every total, and the UI
 * divides by the currency's minor unit exactly once, at the moment of printing.
 */
/** Currencies whose minor unit is not 1/100. Extend as markets are added. */
const MINOR_UNITS = {
    SAR: 2,
    AED: 2,
    USD: 2,
    EUR: 2,
    KWD: 3,
    BHD: 3,
    OMR: 3,
    JPY: 0,
};
export function minorUnitDigits(currency) {
    return MINOR_UNITS[currency.toUpperCase()] ?? 2;
}
function localeTag(locale, numerals) {
    if (locale !== 'ar')
        return 'en-SA';
    return numerals === 'latin' ? 'ar-SA-u-nu-latn' : 'ar-SA-u-nu-arab';
}
/** Formats minor units as a localized currency string. */
export function formatMoney(amountMinor, currency, locale = 'en', options = {}) {
    const digits = minorUnitDigits(currency);
    const value = amountMinor / 10 ** digits;
    const isWhole = amountMinor % 10 ** digits === 0;
    const fractionDigits = options.hideDecimalsWhenWhole && isWhole ? 0 : digits;
    return new Intl.NumberFormat(localeTag(locale, options.numerals ?? 'native'), {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(value);
}
/**
 * A plain count — order counts, stock levels, page numbers.
 *
 * Separate from `formatMoney` because a count has no currency and no minor
 * unit: passing one through the money path would divide it by a hundred.
 */
export function formatNumber(value, locale = 'en', options = {}) {
    return new Intl.NumberFormat(localeTag(locale, options.numerals ?? 'native'), {
        maximumFractionDigits: options.maximumFractionDigits ?? 0,
    }).format(value);
}
/** A percentage for a trend chip: `+3%`. Sign is explicit — a trend needs it. */
export function formatPercent(value, locale = 'en', options = {}) {
    return new Intl.NumberFormat(localeTag(locale, options.numerals ?? 'native'), {
        style: 'percent',
        maximumFractionDigits: 1,
        signDisplay: 'exceptZero',
    }).format(value / 100);
}
/** The plain number a price input should show when editing minor units. */
export function toMajorUnits(amountMinor, currency) {
    return amountMinor / 10 ** minorUnitDigits(currency);
}
/** Rating stored in hundredths of a star (450 → 4.5). */
export function ratingToStars(ratingAvgHundredths) {
    return ratingAvgHundredths / 100;
}
/** Percentage saved, rounded to a whole number for a discount badge. */
export function discountPercent(original, current) {
    if (original <= 0 || current >= original)
        return 0;
    return Math.round(((original - current) / original) * 100);
}
//# sourceMappingURL=money.js.map